import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { toggleMobileSidebar } from 'containers/App/actions';
import { makeSelectMobileSidebarOpen, makeSelectNotificationBannerHeight } from 'containers/App/selectors';
import { createStructuredSelector } from 'reselect';
import PropTypes from 'prop-types';
import orderBy from 'lodash/orderBy';
import {
  HOCs,
  MenuListItem,
} from '@pearsontechnology/aero-components';
import Sidebar, { SidebarOverlay } from 'components/Sidebar';
import enhanceWithThemes from 'hocs/enhanceWithThemes';
import workspaceThemes from 'containers/App/themes';
import workspaceShape from 'Workspace/types/workspace';
import { selectWorkspacesList, selectWorkspace } from 'Workspace/Admin/store/selectors';
import workspaceService from 'services/WorkspaceService';
import { Transition } from 'react-transition-group';
import themes from '../themes';
import {
  Menu,
  MenuItemTitle,
} from './styles';
import { featureKeys } from 'services/FeatureFlagService';
import SubscriptionPlaceholder from './components/SubscriptionPlaceholder';
import enhanceWithFeatureFlagSubscription from 'hocs/enhanceWithFeatureFlagSubscription';

const { enhanceWithPopper } = HOCs;
const SubscriptionPlaceholderWithPopper = enhanceWithPopper(SubscriptionPlaceholder);
const ThemedMenu = enhanceWithThemes(Menu, workspaceThemes);
const {FEATURE_FLAG_BRNT_19838_WORKSPACE_ALERT_BANNER_DO_NOT_DELETE,
  BRNT_19837_WORKSPACE_NOTIFICATION_BANNER_DO_NOT_DELETE,
  BRNT_19839_WORKSPACE_WARNING_BANNER_DO_NOT_DELETE} = featureKeys;

class WorkspaceSidebar extends Component {
  constructor(props) {
    super(props);
    this.sideBarRef = React.createRef();
    this.sideBarHeight = 0;
  }

  componentDidMount() {
    const { toggleSidebar, notificationBannerHeight } = this.props;
    document.addEventListener('keydown', this.handleKeydown);
    setTimeout(() => toggleSidebar(false));
    this.setSideBarHeight(notificationBannerHeight);
  }

  componentDidUpdate() {
    this.setSideBarHeight(this.props.notificationBannerHeight);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeydown);
  }

   getBannerHeight = () => {
     const boundingRect = this.sideBarRef.current.getBoundingClientRect();
     const { height } = boundingRect;
     this.sideBarHeight = height;
   } 

  setSideBarHeight = (bannerHeight) => {
    const featureFlags = this.props.featureFlags;
    if(featureFlags?.[FEATURE_FLAG_BRNT_19838_WORKSPACE_ALERT_BANNER_DO_NOT_DELETE] ||
      featureFlags?.[BRNT_19837_WORKSPACE_NOTIFICATION_BANNER_DO_NOT_DELETE] ||
      featureFlags?.[BRNT_19839_WORKSPACE_WARNING_BANNER_DO_NOT_DELETE]){
      if(!this.sideBarHeight) {
        this.getBannerHeight();
      }
      const finalHeight = this.sideBarHeight - bannerHeight;
      this.sideBarRef.current.style.height = finalHeight+'px';
    }
  };

  selectWorkspace = (workspace) => {
    workspaceService.setSelectedWorkspace(workspace);
    this.props.history.push('/');
  };

  handleKeydown = (event) => {
    const { toggleSidebar } = this.props;

    if (event.code === 'Escape') {
      toggleSidebar(false);
    }
  };

  render() {
    const { navigation: Navigation, workspaces, toggleSidebar, mobileSidebarOpen, ...props } = this.props;
    const selectedWorkspace = workspaceService.getSelectedWorkspace();
    const selectedWorkspaceId = (selectedWorkspace && selectedWorkspace.id) || '';
    const sortedWorkspaces = orderBy(workspaces, (workspace) =>
      `${workspace.name.trim().toLowerCase()}-${workspace.id}`, 'asc');
    const selectedWorkspaceName = (selectedWorkspace && selectedWorkspace.name) || '';

    return (
      <Fragment>
        <Transition
          mountOnEnter
          unmountOnExit
          in={mobileSidebarOpen}
          onEnter={(node) => node && node.offsetHeight}
          timeout={400}
        >
          {(status) =>
            <SidebarOverlay status={status} onClick={() => toggleSidebar(false)} />
          }
        </Transition>
        <Sidebar ref={this.sideBarRef} mobileSidebarOpen={mobileSidebarOpen} {...props}>
          { Navigation }
          <SubscriptionPlaceholderWithPopper placement="top" workspaceName={selectedWorkspaceName} hidepopperonclick>
            {
              <ThemedMenu>
                {
                  sortedWorkspaces && sortedWorkspaces.map((workspace) => {
                    const selected = workspace.id === selectedWorkspaceId;
                    return (
                      <MenuListItem
                        key={workspace.id}
                        selected={selected}
                        onClick={() => this.selectWorkspace(workspace)}
                      >
                        <MenuItemTitle>
                          {workspace.name}
                        </MenuItemTitle>
                      </MenuListItem>
                    );
                  })
                }
              </ThemedMenu>
            }
          </SubscriptionPlaceholderWithPopper>
        </Sidebar>
      </Fragment>
    );
  }
}

WorkspaceSidebar.propTypes = {
  navigation: PropTypes.element,
  workspaces: PropTypes.arrayOf(workspaceShape),
  toggleSidebar: PropTypes.func,
  mobileSidebarOpen: PropTypes.bool,
  currentWorkspace: PropTypes.object,
};

export const mapStateToProps = createStructuredSelector({
  workspaces: selectWorkspacesList,
  notificationBannerHeight: makeSelectNotificationBannerHeight(),
  mobileSidebarOpen: makeSelectMobileSidebarOpen(),
  currentWorkspace: selectWorkspace,
});

export function mapDispatchToProps(dispatch) { // Exported for testing purposes.
  return {
    dispatch,
    toggleSidebar: (value) => dispatch(toggleMobileSidebar(value)),
  };
}
const flagNames = [featureKeys.FEATURE_FLAG_BRNT_19838_WORKSPACE_ALERT_BANNER_DO_NOT_DELETE, featureKeys.BRNT_19837_WORKSPACE_NOTIFICATION_BANNER_DO_NOT_DELETE, BRNT_19839_WORKSPACE_WARNING_BANNER_DO_NOT_DELETE];
const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default compose(withConnect, withRouter)(enhanceWithThemes(enhanceWithFeatureFlagSubscription(WorkspaceSidebar, flagNames), themes));
